import React, { useState, useEffect } from 'react';
import { mockTOULibrary, mockVehiclePricing } from '../../../data/mockScenarioData';
import LineChartComponent from '../../pro_calculator/inputs/common/LineChart';
import { initialEnergyChargeData } from '../../pro_calculator/inputs/EnergyRate';
import UtilityRatePickerDrawer from './UtilityRatePickerDrawer';
import HeatmapGrid from '../../data_hub/HeatmapGrid';
import RateTable from '../../data_hub/RateTable';
import { flattenTieredStrux } from '../../../hooks/useUtilityRates';
import { FaSearch, FaInfoCircle, FaTimes } from 'react-icons/fa';

/** @param {'rate'|'managed'|'vehicle'|'all'} section - when set, only that section is rendered (for workflow dedicated pages) */
const EconomicTab = ({ data, updateData, section = 'all' }) => {
  const showRate = section === 'all' || section === 'rate';
  const showManaged = section === 'all' || section === 'managed';
  const showVehicle = section === 'all' || section === 'vehicle';
  const [selectedTOU, setSelectedTOU] = useState(data.touDatasetId || null);
  const [touRates, setTouRates] = useState(data.touRates || initialEnergyChargeData);
  const [v2gMode, setV2gMode] = useState(
    data.v2gMode === 'active_managed_v2g' ? 'active_managed' : (data.v2gMode || 'unmanaged')
  );
  const [depotChargingPower, setDepotChargingPower] = useState(data.depotChargingPower ?? 50);
  const [enrouteChargingPower, setEnrouteChargingPower] = useState(data.enrouteChargingPower ?? 350);
  const [vehiclePricing, setVehiclePricing] = useState(data.vehiclePricingDatasetId || null);
  const [showTOUChart, setShowTOUChart] = useState(false);
  
  // Datahub rate picker state
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [datahubRate, setDatahubRate] = useState(null);
  
  // Initialize from data if available
  useEffect(() => {
    if (data.touSource === 'datahub' && data.touRateMeta) {
      setDatahubRate({
        meta: data.touRateMeta,
        details: data.touRateDetails,
      });
    }
  }, []);

  useEffect(() => {
    const source = datahubRate ? 'datahub' : (selectedTOU ? 'library' : data.touSource || 'library');
    
    updateData({
      touSource: source,
      touDatasetId: datahubRate ? datahubRate.meta.id : selectedTOU,
      touRates: touRates,
      touRateMeta: datahubRate ? datahubRate.meta : null,
      touRateDetails: datahubRate ? datahubRate.details : null,
      v2gMode: v2gMode,
      depotChargingPower: Number(depotChargingPower) || 50,
      enrouteChargingPower: Number(enrouteChargingPower) || 350,
      vehiclePricingDatasetId: vehiclePricing
    });
  }, [selectedTOU, touRates, v2gMode, depotChargingPower, enrouteChargingPower, vehiclePricing, datahubRate]);

  const handleTOUSelection = (touId) => {
    setSelectedTOU(touId);
    const selected = mockTOULibrary.find(t => t.id === touId);
    if (selected) {
      setTouRates(selected.rates);
      setShowTOUChart(true);
    }
  };

  const handleVehiclePricingSelection = (pricingId) => {
    setVehiclePricing(pricingId);
  };

  const handleSelectDatahubRate = (rateData) => {
    // Extract relevant data from URDB rate
    const meta = {
      id: rateData.id,
      utility_name: rateData.utility_name,
      rate_name: rateData.rate_name,
      sector: rateData.sector,
      effective_date: rateData.effective_date,
    };
    
    setDatahubRate({
      meta,
      details: rateData.rate_details,
    });
    
    // Clear legacy selections
    setSelectedTOU(null);
    setShowTOUChart(false);
  };

  const handleClearDatahubRate = () => {
    setDatahubRate(null);
  };

  const selectedTOUData = mockTOULibrary.find(t => t.id === selectedTOU);
  const selectedVehiclePricingData = mockVehiclePricing.find(p => p.id === vehiclePricing);

  return (
    <div className="space-y-8">
      {/* Time of Use Rates Section */}
      {showRate && (
      <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
        <div className="mb-4">
          <h3 className="text-xl font-bold text-gray-800 font-ui">Electric Utility Rates</h3>
          <p className="text-sm text-gray-600 mt-1">Select an electric utility rate from the database and make modifications.</p>
        </div>
        
        {/* Datahub Rate Selection */}
        {datahubRate ? (
          <div className="mb-4">
            <div className="bg-white rounded-lg p-4 border-2 border-blue-500">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3">
                  <FaInfoCircle className="text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">{datahubRate.meta.rate_name}</h4>
                    <p className="text-sm text-gray-700">{datahubRate.meta.utility_name}</p>
                    <div className="flex gap-3 mt-2 text-xs text-gray-600">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-medium">
                        {datahubRate.meta.sector}
                      </span>
                      <span>Effective: {new Date(datahubRate.meta.effective_date).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleClearDatahubRate}
                  className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                  title="Remove rate"
                >
                  <FaTimes />
                </button>
              </div>
              
              {/* Preview of selected rate */}
              {datahubRate.details && (
                <div className="mt-4 space-y-4">
                  {/* Energy Rate Structure Preview */}
                  {datahubRate.details.energyRateStrux && (
                    <div className="bg-gray-50 rounded p-3">
                      <RateTable
                        data={flattenTieredStrux(datahubRate.details.energyRateStrux, 'energyRateTiers').slice(0, 5)}
                        title="Energy Rate Structure (Preview - First 5 rows)"
                        columns={[
                          { key: 'period', label: 'Period', format: 'integer' },
                          { key: 'tier', label: 'Tier', format: 'integer' },
                          { key: 'rate', label: 'Rate ($/kWh)', format: 'currency' },
                        ]}
                      />
                    </div>
                  )}
                  
                  {/* Weekday Schedule Preview */}
                  {datahubRate.details.energyWeekdaySched && (
                    <div className="bg-gray-50 rounded p-3">
                      <HeatmapGrid
                        schedule={datahubRate.details.energyWeekdaySched}
                        title="Weekday TOU Schedule"
                      />
                    </div>
                  )}
                  
                  {/* Weekend Schedule Preview */}
                  {datahubRate.details.energyWeekendSched && (
                    <div className="bg-gray-50 rounded p-3">
                      <HeatmapGrid
                        schedule={datahubRate.details.energyWeekendSched}
                        title="Weekend TOU Schedule"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <button
              onClick={() => setIsPickerOpen(true)}
              className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
            >
              <FaSearch />
              <span>Browse Different Rate</span>
            </button>
          </div>
        ) : (
          <>
            <button
              onClick={() => setIsPickerOpen(true)}
              className="mb-4 w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-semibold"
            >
              <FaSearch />
              <span>Browse Electric Utility Rates</span>
            </button>
            
            {/* <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-50 text-gray-500">or use simple schedule</span>
              </div>
            </div> */}
            
            {/* <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Select TOU Rate Schedule (Legacy)
              </label>
              <select
                value={selectedTOU || ''}
                onChange={(e) => handleTOUSelection(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Choose from TOU library...</option>
                {mockTOULibrary.map(tou => (
                  <option key={tou.id} value={tou.id}>
                    {tou.name} - {tou.utility} ({tou.state})
                  </option>
                ))}
              </select> */}
              
              {/* {selectedTOUData && (
                <div className="mt-3 p-3 bg-white rounded border border-gray-300">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Utility:</span> {selectedTOUData.utility}
                  </p>
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Description:</span> {selectedTOUData.description}
                  </p>
                </div>
              )}
            </div> */}

            {/* {showTOUChart && touRates && (
              <div className="mt-4 bg-white rounded-lg p-4 border border-gray-200">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">TOU Rate Schedule Preview</h4>
                <LineChartComponent
                  xLabel="Time of Day (Hour)"
                  yLabel="Energy Charge ($/kWh)"
                  initialData={touRates}
                  onDataUpdate={setTouRates}
                />
              </div>
            )}

            <div className="mt-4">
              <label className="block text-sm font-semibold text-gray-600 mb-2">
                Or upload your own TOU schedule (CSV)
              </label>
              <input
                type="file"
                accept=".csv"
                className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
              />
            </div> */}
          </>
        )}
      </div>
      )}
      
      {/* Rate Picker Drawer */}
      {showRate && (
      <UtilityRatePickerDrawer
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        onSelectRate={handleSelectDatahubRate}
      />
      )}

      {/* V2G Capabilities Section */}
      {showManaged && (
      <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
        <div className="mb-4">
          <h3 className="text-xl font-bold text-gray-800 font-ui">Managed Charging Options</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Depot charging power (kW)
            </label>
            <input
              type="number"
              min={1}
              max={2000}
              step={1}
              value={depotChargingPower}
              onChange={(e) => setDepotChargingPower(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              En-route charging power (kW)
            </label>
            <input
              type="number"
              min={1}
              max={2000}
              step={1}
              value={enrouteChargingPower}
              onChange={(e) => setEnrouteChargingPower(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="space-y-3">
          {[
            {
              value: 'unmanaged',
              label: 'Unmanaged',
              description: 'Vehicles charge immediately when plugged in without any coordination'
            },
            {
              value: 'passive_managed',
              label: 'Passive Managed',
              description: 'Charging responds to TOU rates, but no load smoothing'
            },
            {
              value: 'active_managed',
              label: 'Active Managed',
              description: 'Charging responds to TOU rates with load smoothing'
            },
            {
              value: 'active_managed_v2g',
              label: 'Active Managed (V2G)',
              description: 'Full bidirectional power flow - vehicles can discharge to support grid'
            }
          ].map(option => {
            const isInDevelopment = option.value === 'active_managed_v2g';
            return (
            <label
              key={option.value}
              className={`flex items-start p-4 border-2 rounded-lg transition-all ${
                isInDevelopment
                  ? 'cursor-not-allowed opacity-70'
                  : 'cursor-pointer'
              } ${
                v2gMode === option.value
                  ? 'border-gray-900 bg-white'
                  : 'border-gray-200 bg-white hover:border-gray-400'
              }`}
            >
              <input
                type="radio"
                name="v2g"
                value={option.value}
                checked={v2gMode === option.value}
                disabled={isInDevelopment}
                onChange={(e) => setV2gMode(e.target.value)}
                className="mt-1 mr-3"
              />
              <div>
                <p className="font-semibold text-gray-800 inline-flex items-center gap-2">
                  <span>{option.label}</span>
                  {isInDevelopment && (
                    <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                      in development
                    </span>
                  )}
                </p>
                <p className="text-sm text-gray-600">{option.description}</p>
              </div>
            </label>
            );
          })}
        </div>
      </div>
      )}

      {/* Vehicle Pricing Section */}
      {showVehicle && (
      <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
        <div className="mb-4">
          <h3 className="text-xl font-bold text-gray-800 font-ui">Vehicle Pricing</h3>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Select Vehicle Price Dataset
          </label>
          <select
            value={vehiclePricing || ''}
            onChange={(e) => handleVehiclePricingSelection(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
          >
            <option value="">Choose pricing dataset...</option>
            {mockVehiclePricing.map(pricing => (
              <option key={pricing.id} value={pricing.id}>
                {pricing.name} (Year: {pricing.year})
              </option>
            ))}
          </select>
        </div>

        {selectedVehiclePricingData && (
          <div className="mt-4 bg-white rounded-lg p-4 border border-gray-300">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">
              {selectedVehiclePricingData.name} - Vehicle Prices
            </h4>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Vehicle Class</th>
                    <th className="px-4 py-2 text-right font-semibold text-gray-700">MSRP</th>
                    <th className="px-4 py-2 text-right font-semibold text-gray-700">Incentive</th>
                    <th className="px-4 py-2 text-right font-semibold text-gray-700">Net Price</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedVehiclePricingData.vehicles.map((vehicle, idx) => (
                    <tr key={idx} className="border-t border-gray-200">
                      <td className="px-4 py-2 text-gray-800">{vehicle.class}</td>
                      <td className="px-4 py-2 text-right text-gray-700">
                        ${vehicle.msrp.toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-700">
                        -${vehicle.incentive.toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-right font-semibold text-gray-800">
                        ${(vehicle.msrp - vehicle.incentive).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="mt-4">
          <label className="block text-sm font-semibold text-gray-600 mb-2">
            Or upload your own vehicle pricing table (CSV)
          </label>
          <input
            type="file"
            accept=".csv"
            className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold             file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
          />
        </div>
      </div>
      )}
    </div>
  );
};

export default EconomicTab;
